import React, { useRef, useState, useEffect, useCallback } from 'react';

/* Barra de Herramientas de Dibujo (estilo TradingView) sobre lightweight-charts.
   Herramientas: cursor/crosshair, línea de tendencia, horizontal, canal,
   rectángulo, Fibonacci (Gann/geometría y proyección), regla (Δprecio, Δ%, Δvelas),
   Long/Short con Riesgo:Beneficio, pincel libre, texto, emojis, zoom +/- y borrar. */
const TOOLS = [
  ['cursor', '✛', 'Cursor / Crosshair'],
  ['trend', '╱', 'Línea de tendencia'],
  ['hline', '─', 'Línea horizontal (soporte/resistencia)'],
  ['channel', '∥', 'Canal paralelo'],
  ['rect', '▭', 'Rectángulo / zona'],
  ['fib', 'φ', 'Fibonacci retroceso (proyección de precios)'],
  ['ruler', '📏', 'Regla: precio, % y tiempo'],
  ['long', 'L', 'Long Position (riesgo/beneficio)'],
  ['short', 'S', 'Short Position (riesgo/beneficio)'],
  ['brush', '✎', 'Pincel / dibujo libre'],
  ['text', 'T', 'Texto y notas'],
  ['emoji', '☻', 'Emojis e iconos'],
];
const EMOJIS = ['🚀', '🔥', '⚠️', '⬆️', '⬇️', '💰', '🐻', '🐂'];
const FIBS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

const DrawingTools = ({ chartRef, width, height }) => {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('cursor');
  const [emoji, setEmoji] = useState('🚀');
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);

  const px2price = (y) => {
    try { return chartRef.current?.series?.coordinateToPrice(y); } catch { return null; }
  };
  const px2time = (x) => {
    try { return chartRef.current?.chart?.timeScale().coordinateToTime(x); } catch { return null; }
  };

  const draw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    const all = draft ? [...shapes, draft] : shapes;
    all.forEach((s) => {
      ctx.lineWidth = 1.4; ctx.font = '12px sans-serif';
      ctx.strokeStyle = s.t === 'short' ? '#d8493f' : s.t === 'long' ? '#0eaa6b' : '#0a7bd4';
      ctx.fillStyle = ctx.strokeStyle;
      const { x1, y1, x2 = x1, y2 = y1 } = s;
      if (s.t === 'trend') { ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }
      if (s.t === 'hline') {
        ctx.beginPath(); ctx.moveTo(0, y1); ctx.lineTo(cv.width, y1); ctx.stroke();
        const p = px2price(y1); if (p) ctx.fillText(p.toFixed(2), 6, y1 - 4);
      }
      if (s.t === 'channel') {
        const dy = (y2 - y1) * 0.25;
        [0, 1].forEach((k) => { ctx.beginPath(); ctx.moveTo(x1, y1 + k * dy * 4 - dy * 2); ctx.lineTo(x2, y2 + k * dy * 4 - dy * 2); ctx.stroke(); });
      }
      if (s.t === 'rect') { ctx.globalAlpha = 0.15; ctx.fillRect(x1, y1, x2 - x1, y2 - y1); ctx.globalAlpha = 1; ctx.strokeRect(x1, y1, x2 - x1, y2 - y1); }
      if (s.t === 'fib') {
        const p1 = px2price(y1), p2 = px2price(y2);
        FIBS.forEach((f) => {
          const y = y1 + (y2 - y1) * f;
          ctx.globalAlpha = 0.7; ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke(); ctx.globalAlpha = 1;
          const pr = p1 && p2 ? (p1 + (p2 - p1) * f).toFixed(2) : '';
          ctx.fillText(`${(f * 100).toFixed(1)}%  ${pr}`, Math.min(x1, x2) + 4, y - 3);
        });
      }
      if (s.t === 'ruler') {
        ctx.setLineDash([5, 4]); ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.setLineDash([]);
        const p1 = px2price(y1), p2 = px2price(y2), t1 = px2time(x1), t2 = px2time(x2);
        if (p1 && p2) {
          const dp = p2 - p1, pct = (dp / p1) * 100;
          const velas = t1 && t2 ? Math.round(Math.abs(t2 - t1) / 300) : 0;
          ctx.fillStyle = '#14253a';
          ctx.fillText(`Δ ${dp.toFixed(2)} (${pct.toFixed(2)}%) · ${velas} velas`, (x1 + x2) / 2 + 6, (y1 + y2) / 2);
        }
      }
      if (s.t === 'long' || s.t === 'short') {
        const entry = px2price(y1), target = px2price(y2);
        const stopY = y1 + (y1 - y2) / 2;
        const stop = px2price(stopY);
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = '#0eaa6b'; ctx.fillRect(x1, Math.min(y1, y2), 130, Math.abs(y2 - y1));
        ctx.fillStyle = '#d8493f'; ctx.fillRect(x1, Math.min(y1, stopY), 130, Math.abs(stopY - y1));
        ctx.globalAlpha = 1; ctx.fillStyle = '#14253a';
        if (entry && target && stop) {
          const rr = Math.abs((target - entry) / (entry - stop || 1)).toFixed(2);
          ctx.fillText(`${s.t.toUpperCase()} · entrada ${entry.toFixed(2)}`, x1 + 4, y1 - 4);
          ctx.fillText(`objetivo ${target.toFixed(2)} · stop ${stop.toFixed(2)} · R:B ${rr}`, x1 + 4, y2 - 4);
        }
      }
      if (s.t === 'brush' && s.pts) {
        ctx.beginPath(); s.pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.stroke();
      }
      if (s.t === 'text') { ctx.fillStyle = '#14253a'; ctx.font = '13px sans-serif'; ctx.fillText(s.txt || '', x1, y1); }
      if (s.t === 'emoji') { ctx.font = '22px sans-serif'; ctx.fillText(s.txt, x1 - 11, y1 + 8); }
    });
  }, [shapes, draft]);

  useEffect(draw, [draw, width, height]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const down = (e) => {
    if (tool === 'cursor') return;
    const [x, y] = pos(e);
    if (tool === 'text') {
      const txt = window.prompt('Nota / etiqueta:'); 
      if (txt) setShapes((s) => [...s, { t: 'text', x1: x, y1: y, txt }]);
      return;
    }
    if (tool === 'emoji') { setShapes((s) => [...s, { t: 'emoji', x1: x, y1: y, txt: emoji }]); return; }
    if (tool === 'hline') { setShapes((s) => [...s, { t: 'hline', x1: x, y1: y }]); return; }
    setDraft(tool === 'brush' ? { t: 'brush', pts: [[x, y]] } : { t: tool, x1: x, y1: y, x2: x, y2: y });
  };
  const move = (e) => {
    if (!draft) return;
    const [x, y] = pos(e);
    setDraft((d) => (d.t === 'brush' ? { ...d, pts: [...d.pts, [x, y]] } : { ...d, x2: x, y2: y }));
  };
  const up = () => { if (draft) { setShapes((s) => [...s, draft]); setDraft(null); } };
  const zoom = (dir) => {
    try {
      const ts = chartRef.current.chart.timeScale();
      const r = ts.getVisibleLogicalRange();
      const span = (r.to - r.from) * (dir > 0 ? 0.7 : 1.4);
      const mid = (r.to + r.from) / 2;
      ts.setVisibleLogicalRange({ from: mid - span / 2, to: mid + span / 2 });
    } catch {}
  };

  return (
    <div className="flex">
      <div className="flex flex-col gap-1 mr-1">
        {TOOLS.map(([id, ic, tip]) => (
          <button key={id} title={tip} onClick={() => setTool(id)}
            className={`w-7 h-7 text-sm rounded border ${tool === id ? 'bg-[#0a7bd4] text-white border-transparent' : 'border-[#27272A] text-[#A1A1AA]'}`}>
            {ic}
          </button>
        ))}
        <button title="Zoom +" onClick={() => zoom(1)} className="w-7 h-7 rounded border border-[#27272A] text-[#A1A1AA]">＋</button>
        <button title="Zoom −" onClick={() => zoom(-1)} className="w-7 h-7 rounded border border-[#27272A] text-[#A1A1AA]">－</button>
        <button title="Borrar dibujos" onClick={() => setShapes([])} className="w-7 h-7 rounded border border-[#27272A] text-[#A1A1AA]">🗑</button>
        {tool === 'emoji' && (
          <select value={emoji} onChange={(e) => setEmoji(e.target.value)} className="w-7 text-sm">
            {EMOJIS.map((e2) => <option key={e2}>{e2}</option>)}
          </select>
        )}
      </div>
      <canvas ref={canvasRef} width={width} height={height}
        onMouseDown={down} onMouseMove={move} onMouseUp={up}
        style={{ position: 'absolute', left: 34, top: 0, zIndex: 5,
                 pointerEvents: tool === 'cursor' ? 'none' : 'auto', cursor: 'crosshair' }} />
    </div>
  );
};

export default DrawingTools;
